import React, { useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ArtworkUpload } from "../components/ArtworkUpload";
import { MultiArtwork } from "../components/MultiArtwork";
import { customStickerPrice, stickerSheetPrice, CUSTOM_CONFIG, SHEET_SIZES } from "../data/customStickerPricing";
import { formatCents } from "../data/pricing";
import { formatAUDexact } from "../data/signsPricing";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";

const TYPES = [
  { id: "circle", label: "Circle or Oval", subs: [["circle", "Circle"], ["oval", "Oval"]], icon: "rounded-full" },
  { id: "square", label: "Square or Rectangle", subs: [["square", "Square"], ["rectangle", "Rectangle"]], icon: "rounded-md" },
  { id: "custom", label: "Custom Shape", icon: "rounded-[40%_60%_55%_45%/55%_40%_60%_45%]" },
  { id: "sheet", label: "Sticker Sheet", icon: "rounded-md sheet" },
];
const SUB_LABEL = { circle: "Circle", oval: "Oval", square: "Square", rectangle: "Rectangle", custom: "Custom Shape", sheet: "Sticker Sheet" };
const toInt = (v) => v.replace(/\D/g, "").slice(0, 6);
const MODES = {
  diecut: { types: ["circle", "square", "custom"], eyebrow: "Die-cut stickers", title: ["Your artwork.", "Your shape.", "Your quantity."], intro: "Choose a sticker type, enter any size and quantity, and get an instant price.", image: "/products/custom-stickers.webp", imageLabel: "Die-cut sticker example" },
  sheets: { types: ["sheet"], eyebrow: "Stickers on sheets", title: ["Your designs.", "On one sheet.", "Easy to peel."], intro: "Multiple stickers printed together on convenient A6, A5 or A4 sheets. Choose a sheet size and quantity for an instant price.", image: "/products/stickers-on-sheets.webp", imageLabel: "Sticker sheet example" },
};

export default function Custom({ mode = "diecut" }) {
  const M = MODES[mode];
  const [type, setType] = useState(M.types[0]);
  const [sub, setSub] = useState(M.types[0]);
  const [dims, setDims] = useState({ a: "", b: "" });
  const [qty, setQty] = useState("500");
  const [sheetSize, setSheetSize] = useState("A4");
  const [kinds, setKinds] = useState("1");
  const [art, setArt] = useState({ mode: "combined", file: null, files: [], confirmed: false });
  const [copyright, setCopyright] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const isSheet = type === "sheet";
  const single = sub === "circle" || sub === "square";
  const width = isSheet ? SHEET_SIZES[sheetSize].width : dims.a;
  const height = isSheet ? SHEET_SIZES[sheetSize].height : single ? dims.a : dims.b;
  const k = isSheet ? 1 : parseInt(kinds, 10) || 0;
  const q = parseInt(qty, 10) || 0;

  const result = useMemo(() => {
    if (!width || !height) return { status: "invalid", message: single ? "Enter your sticker size in millimetres." : "Enter both width and height in millimetres." };
    return isSheet
      ? stickerSheetPrice({ size: sheetSize, sheets: q })
      : customStickerPrice({ type, width, height, qtyPerDesign: q, kinds: k });
  }, [isSheet, sheetSize, type, single, width, height, q, k]);

  const chooseType = (t) => {
    setType(t.id);
    setSub(t.subs ? t.subs[0][0] : t.id);
    setDims({ a: "", b: "" });
  };
  const sizeLabel = isSheet ? `${sheetSize} (${SHEET_SIZES[sheetSize].mm})` : width && height ? (sub === "circle" ? `${width} mm diameter` : `${width} × ${height} mm`) : "—";
  const total = result.total ?? (isSheet ? q : q * k);

  const addToCart = () => {
    if (result.status === "invalid") { toast.error(result.message); return; }
    const separate = art.mode === "separate" && k > 1;
    const files = separate ? Array.from({ length: k }, (_, i) => art.files[i]) : art.file ? [{ ...art.file, design: k > 1 ? `All ${k} designs` : "Design 1" }] : [];
    if (separate) {
      if (files.length < k || files.some((f) => !f)) { toast.error(`Please upload a file for each of your ${k} designs`); return; }
    } else if (!art.file) { toast.error("Please upload your artwork first"); return; }
    if (k > 1 && art.mode === "combined" && !art.confirmed) { toast.error(`Please confirm your file contains all ${k} designs`); return; }
    if (!copyright) { toast.error("Please confirm you own or have permission to print this artwork"); return; }
    const quote = result.status === "quote";
    addItem({
      product_type: "custom",
      product_name: isSheet ? "Custom Sticker Sheet" : type === "custom" ? "Custom Shape Sticker" : `Custom ${SUB_LABEL[sub]} Sticker`,
      shape: SUB_LABEL[sub],
      size: isSheet ? `${sheetSize} · ${SHEET_SIZES[sheetSize].mm}` : sub === "circle" ? `${width} mm` : `${width} × ${height} mm`,
      width_mm: Number(width),
      height_mm: Number(height),
      quantity: total,
      qty_per_design: isSheet ? null : q,
      kinds: isSheet ? null : k,
      unit_price: quote ? 0 : result.price / total,
      line_total: quote ? 0 : result.price,
      needs_quote: quote,
      artwork_file_id: files[0].id,
      artwork_filename: files.length > 1 ? `${files.length} files` : files[0].name,
      artwork_files: files.map((f) => ({ file_id: f.id, filename: f.name, design: f.design })),
      artwork_mode: k > 1 ? art.mode : "single",
      copyright_confirmed: true,
    });
    toast.success(quote ? "Added — we'll send you a quote" : "Custom stickers added to cart");
    navigate("/cart");
  };

  return (
    <div className="App">
      <Header />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="eyebrow" data-testid="custom-mode-eyebrow">{M.eyebrow}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-2 tracking-tight">{M.title[0]}<br />{M.title[1]}<br />{M.title[2]}</h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl">{M.intro}</p>
        </div>
        <div className="lg:col-span-5">
          <div className="sf-card overflow-hidden">
            <div className="aspect-[5/4] relative bg-[#F3EFEA]">
              <img src={M.image} alt={M.imageLabel} className="w-full h-full object-cover" data-testid="custom-hero-photo" />
              <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/80 backdrop-blur text-white text-[10px] font-bold uppercase tracking-widest">{M.imageLabel}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-10">
          {!isSheet && <Step n={1} title="Choose your sticker type">
            <div className="grid grid-cols-3 gap-3" data-testid="custom-type-options">
              {TYPES.filter((t) => M.types.includes(t.id)).map((t) => (
                <button key={t.id} onClick={() => chooseType(t)} aria-pressed={type === t.id} data-testid={`custom-type-${t.id}`}
                  className={`rounded-2xl border-2 p-4 flex flex-col items-center gap-3 text-center transition-colors ${type === t.id ? "border-[color:var(--brand)] bg-[color:var(--brand-soft)]" : "border-[#E2DDD5] bg-white hover:border-[color:var(--brand)]"}`}>
                  <TypeIcon id={t.id} />
                  <span className="font-display font-bold text-sm leading-tight">{t.label}</span>
                </button>
              ))}
            </div>
          </Step>}

          <Step n={isSheet ? 1 : 2} title={isSheet ? "Choose your sheet size" : "Enter your size"}>
            {TYPES.find((t) => t.id === type).subs && (
              <div className="flex flex-wrap gap-2 mb-4">
                {TYPES.find((t) => t.id === type).subs.map(([id, label]) => (
                  <button key={id} onClick={() => { setSub(id); }} className={`chip ${sub === id ? "chip-active" : ""}`} data-testid={`custom-sub-${id}`}>{label}</button>
                ))}
              </div>
            )}
            {isSheet ? (
              <div className="flex flex-wrap gap-2" data-testid="custom-sheet-sizes">
                {Object.values(SHEET_SIZES).map((s) => (
                  <button key={s.label} onClick={() => setSheetSize(s.label)} className={`chip ${sheetSize === s.label ? "chip-active" : ""}`} data-testid={`custom-sheet-${s.label}`}>
                    {s.label} <span className="text-[11px] opacity-70 ml-1">{s.mm}</span>
                  </button>
                ))}
              </div>
            ) : (
            <div className={`grid gap-3 ${single ? "grid-cols-1 max-w-xs" : "grid-cols-2 max-w-md"}`}>
              <NumField label={{ circle: "Diameter (mm)", square: "Side length (mm)", custom: "Maximum width (mm)", sheet: "Sheet width (mm)" }[sub] || "Width (mm)"} value={dims.a} onChange={(v) => setDims((d) => ({ ...d, a: v }))} testid="custom-dim-a" placeholder="e.g. 75" />
              {!single && <NumField label={{ custom: "Maximum height (mm)", sheet: "Sheet height (mm)" }[sub] || "Height (mm)"} value={dims.b} onChange={(v) => setDims((d) => ({ ...d, b: v }))} testid="custom-dim-b" placeholder="e.g. 100" />}
            </div>
            )}
            <p className="text-xs text-slate-500 mt-3">
              {type === "custom" ? "Use the widest and tallest points of your shape — we price on the full bounding box." :
                isSheet ? "Your designs arranged on one backing sheet. Enter any number of sheets below." :
                `Sizes from ${CUSTOM_CONFIG.MIN_DIM_MM} mm to ${CUSTOM_CONFIG.MAX_DIM_MM} mm. Price adjusts smoothly with your exact size.`}
            </p>
          </Step>

          <Step n={isSheet ? 2 : 3} title="Choose your quantity">
            <div className={`grid gap-3 items-end ${isSheet ? "grid-cols-1 max-w-xs" : "grid-cols-2 max-w-md"}`}>
              <NumField label={isSheet ? "Number of sheets" : "Quantity per design"} value={qty} onChange={setQty} testid="custom-qty" placeholder="e.g. 500" />
              {!isSheet && <NumField label="Number of different designs (kinds)" value={kinds} onChange={(v) => setKinds(v.slice(0, 3))} testid="custom-kinds" placeholder="1" />}
            </div>
            {!isSheet && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#F3EFEA] px-4 py-2 text-sm font-semibold" data-testid="custom-total-qty">
                {q.toLocaleString()} per design × {k} design{k === 1 ? "" : "s"} = {(q * k).toLocaleString()} stickers
              </div>
            )}
            {!isSheet && <p className="text-xs text-slate-500 mt-2">All designs share the same type and size. Up to {CUSTOM_CONFIG.MAX_KINDS} designs per order.</p>}
          </Step>

          <Step n={isSheet ? 3 : 4} title="Upload your artwork">
            {k > 1 ? (
              <MultiArtwork kinds={k} art={art} setArt={setArt} />
            ) : (
              <ArtworkUpload file={art.file} setFile={(f) => setArt((a) => ({ ...a, file: f }))} testPrefix="custom" />
            )}
            <p className="text-xs text-slate-500 mt-3">{isSheet ? "Upload one file showing the full sheet layout with all your designs." : type === "custom" ? "Your artwork defines the cut line — include a clear outline or we'll create one and confirm with you." : "Print-ready PDF, AI or high-resolution PNG at your chosen size works best."}</p>
          </Step>

          <Step n={isSheet ? 4 : 5} title="Confirm artwork permission">
            <label className={`flex gap-4 items-start p-5 rounded-2xl border-2 cursor-pointer transition-colors ${copyright ? "border-[#10B981] bg-[#10B981]/10" : "border-[color:var(--brand)] bg-white"}`} data-testid="custom-copyright">
              <input type="checkbox" className="sr-only" checked={copyright} onChange={(e) => setCopyright(e.target.checked)} data-testid="custom-copyright-checkbox" />
              <span className={`h-7 w-7 rounded-lg border-2 flex-shrink-0 flex items-center justify-center ${copyright ? "bg-[#10B981] border-[#10B981] text-white" : "border-[color:var(--brand)]"}`} aria-hidden="true">{copyright && <Check size={18} strokeWidth={3} />}</span>
              <span className="font-semibold leading-snug">I confirm that I own this artwork or have permission from the copyright owner to reproduce and print it.</span>
            </label>
          </Step>
        </div>

        <aside className="lg:col-span-5">
          <PriceCard result={result} isSheet={isSheet} minCharge={isSheet || type === "custom" ? 125 : 99} showGst={isSheet || type === "custom"} total={total} onAdd={addToCart}
            rows={[
              ["Type", SUB_LABEL[sub], "custom-summary-shape"],
              ["Size", sizeLabel, "custom-summary-size"],
              ...(isSheet ? [["Sheets", q.toLocaleString(), "custom-summary-qty"]] : [
                ["Quantity per design", q.toLocaleString(), "custom-summary-qty"],
                ["Different designs", String(k), "custom-summary-kinds"],
                ["Total stickers", (q * k).toLocaleString(), "custom-summary-total"],
              ]),
              ["Artwork", k > 1 && art.mode === "separate" ? `${art.files.slice(0, k).filter(Boolean).length} of ${k} files` : art.file ? art.file.name : "Not uploaded", "custom-summary-artwork"],
            ]} />
        </aside>
      </section>
      <MobilePriceBar result={result} onAdd={addToCart} />
      <Footer />
    </div>
  );
}

export function PriceCard({ result, isSheet, isRoll, minCharge, showGst, total, rows, onAdd }) {
  const ok = result.status === "ok";
  return (
    <div className="sf-card p-6 sticky top-20" data-testid="custom-price-card">
      <div className="eyebrow">{isRoll ? "Your price" : "Instant price"}</div>
      {ok && (
        <>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="font-display text-5xl font-black price-tick" data-testid="custom-total-price">{formatAUDexact(result.price)}</div>
            <div className="text-sm text-slate-500 font-semibold">+ GST</div>
          </div>
          <div className="text-slate-600 mt-1" data-testid="custom-per-sticker">{formatCents(result.price / total)} per {isSheet ? "sheet" : "sticker"}</div>
          {result.pricedSize && (
            <div className="mt-3 text-xs text-slate-600 rounded-xl bg-[#F3EFEA] px-3 py-2" data-testid="custom-priced-at">
              Priced for <strong>{result.pricedSize}</strong> × {total.toLocaleString()} {isSheet ? "sheets" : "stickers"}.
            </div>
          )}
          {result.minimumApplied && <div className="mt-2 text-xs font-semibold text-[color:var(--brand)]" data-testid="custom-minimum-note">Minimum order charge of ${result.minCharge || 99} applies.</div>}
          {showGst && <SheetGstRows subtotal={result.price} />}
        </>
      )}
      {result.status === "quote" && (
        <div className="mt-2" data-testid="custom-quote">
          <div className="font-display text-3xl font-black">{isRoll ? "Pricing coming soon" : "Custom quote"}</div>
          <div className="text-sm text-slate-600 mt-1">{result.message} Add it to your cart and we'll come back to you with a price.</div>
        </div>
      )}
      {result.status === "invalid" && <div className="mt-2 text-sm font-semibold text-slate-600" data-testid="custom-price-message">{result.message}</div>}
      <div className="mt-6 space-y-2 text-sm">
        {rows.map(([label, value, testid]) => <Row key={testid} label={label} value={value} testid={testid} />)}
      </div>
      <button onClick={onAdd} className="btn-primary w-full mt-6" data-testid="custom-add-to-cart">{result.status === "quote" ? "Request quote" : "Add to Cart"}</button>
      <p className="text-xs text-slate-500 mt-3">Prices in AUD, excluding GST.{minCharge ? ` Minimum order $${minCharge}.` : ""}</p>
    </div>
  );
}

export function MobilePriceBar({ result, isRoll, onAdd }) {
  return (
    <div className="lg:hidden sticky bottom-0 z-40 bg-white/95 backdrop-blur border-t border-[#E2DDD5] px-5 py-3 flex items-center justify-between gap-3" data-testid="custom-mobile-price-bar">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{isRoll ? "Your price" : "Instant price"}</div>
        <div className="font-display font-black text-xl" data-testid="custom-mobile-price">
          {result.status === "ok" ? <>{formatAUDexact(result.price)} <span className="text-xs font-semibold text-slate-500">+ GST</span></> : result.status === "quote" ? (isRoll ? "Pricing coming soon" : "Custom quote") : "—"}
        </div>
      </div>
      <button onClick={onAdd} className="btn-primary !py-2.5 !px-5 text-sm" data-testid="custom-mobile-add">{result.status === "quote" ? "Request quote" : "Add to Cart"}</button>
    </div>
  );
}

function SheetGstRows({ subtotal }) {
  const gst = Math.round(subtotal * 10) / 100;
  return (
    <div className="mt-4 space-y-1 text-sm" data-testid="custom-sheet-gst">
      <Row label="Subtotal (ex GST)" value={formatAUDexact(subtotal)} testid="custom-sheet-subtotal" />
      <Row label="GST (10%)" value={formatAUDexact(gst)} testid="custom-sheet-gst-amount" />
      <Row label="Total (inc GST)" value={formatAUDexact(Math.round((subtotal + gst) * 100) / 100)} testid="custom-sheet-total-inc" />
    </div>
  );
}

function TypeIcon({ id }) {
  if (id === "sheet") {
    return (
      <div className="h-14 w-12 rounded-md border-2 border-[color:var(--brand)] bg-white grid grid-cols-2 grid-rows-2 gap-1 p-1.5">
        {[0, 1, 2, 3].map((i) => <span key={i} className="rounded-full opacity-70" style={{ backgroundColor: "var(--brand)" }} />)}
      </div>
    );
  }
  const shape = { circle: "h-14 w-14 rounded-full", square: "h-12 w-14 rounded-md", custom: "h-14 w-14 rounded-[40%_60%_55%_45%/55%_40%_60%_45%]" }[id];
  return <div className={`${shape} border-2 border-[color:var(--brand)] bg-[color:var(--brand-soft)]`} />;
}

export function Step({ n, title, children }) {
  return (
    <div>
      <div className="eyebrow mb-3">Step {n} — {title}</div>
      {children}
    </div>
  );
}

export function NumField({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-1">{label}</span>
      <input type="text" inputMode="numeric" value={value} placeholder={placeholder}
        onChange={(e) => onChange(toInt(e.target.value))}
        onKeyDown={(e) => { if (e.key === "." || e.key === ",") e.preventDefault(); }}
        className="w-full px-4 py-3 rounded-xl border border-[#E2DDD5] bg-white focus:outline-none focus:border-[color:var(--brand)]"
        data-testid={testid} />
    </label>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between border-b border-dashed border-[#E2DDD5] py-2" data-testid={testid}>
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-right">{value}</span>
    </div>
  );
}
